/**
 * queryClient.ts — TanStack Query Configuration & Query Keys
 *
 * SOLID Principles Applied:
 * - Single Responsibility: Centralized cache key management
 * - Interface Segregation: Organized key factory by domain
 * - Dependency Inversion: Configuration is externalized
 *
 * Features:
 * - Default query/mutation options
 * - Centralized query key factory for consistent cache keys
 * - Automatic cache invalidation patterns
 * - Type-safe key structure
 */

import { QueryClient, DefaultOptions } from '@tanstack/react-query';

// ─── Default Configuration ─────────────────────────────────────────────────────

const queryConfig: DefaultOptions = {
  queries: {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
    retry: 3, // Retry 3 times on failure (transient server errors may resolve)
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff, max 10s
    refetchOnWindowFocus: false, // Disable on window focus for performance
    refetchOnReconnect: true, // Refetch when network reconnects
    // Refetch stale queries on mount (the React Query default). With `false`
    // here, a save's invalidateQueries only marked unmounted screens stale,
    // and the remount then skipped the stale refetch — so every list screen
    // kept its pre-save rows until a hard refresh. `true` still serves
    // fresh cache (within staleTime) without a request; entry lists that
    // must also survive cross-tab saves opt into `'always'` per query.
    refetchOnMount: true,
  },
  mutations: {
    retry: 1, // Retry mutations once on transient failures (network blips)
  },
};

// ─── Query Client Factory ─────────────────────────────────────────────────────

export const createQueryClient = () => new QueryClient({ defaultOptions: queryConfig });

// ─── Query Key Factory ────────────────────────────────────────────────────────

export const queryKeys = {
  // ─── Auth ───────────────────────────────────────────────────────────────
  auth: {
    all: ['auth'] as const,
    current: () => [...queryKeys.auth.all, 'current'] as const,
  },

  // ─── Users ───────────────────────────────────────────────────────────────
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.users.lists(), { filters }] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },

  // ─── Dashboard ────────────────────────────────────────────────────────────
  dashboards: {
    all: ['dashboards'] as const,
    metrics: () => [...queryKeys.dashboards.all, 'metrics'] as const,
    metric: (roleId: number) => [...queryKeys.dashboards.metrics(), roleId] as const,
  },

  // ─── Customers ───────────────────────────────────────────────────────────
  customers: {
    all: ['customers'] as const,
    lists: () => [...queryKeys.customers.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.customers.lists(), { filters }] as const,
    details: () => [...queryKeys.customers.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.customers.details(), id] as const,
    /**
     * The customer form's combos (symbols, payment-terms, countries, list).
     * Rooted at 'customer' (singular), as these keys always were, so they are
     * not refreshed by a customers save - they are reference data.
     */
    formOptions: (kind: string, ...scope: unknown[]) => ['customer', 'options', kind, ...scope] as const,
  },

  // ─── Suppliers ───────────────────────────────────────────────────────────
  suppliers: {
    all: ['suppliers'] as const,
    lists: () => [...queryKeys.suppliers.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.suppliers.lists(), { filters }] as const,
    details: () => [...queryKeys.suppliers.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.suppliers.details(), id] as const,
    /** The form's combos: symbols, payment-terms, countries, msic-codes, self-billed. */
    options: (kind: string, companyId?: number) =>
      [...queryKeys.suppliers.all, 'options', kind, companyId ?? 0] as const,
  },

  // ─── Employees ───────────────────────────────────────────────────────────
  employees: {
    all: ['employees'] as const,
    lists: () => [...queryKeys.employees.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.employees.lists(), { filters }] as const,
    byCompany: (companyId: number) =>
      [...queryKeys.employees.all, 'company', companyId] as const,
    byCompanyAndRoles: (
      companyId: number,
      roleId?: number,
      roleId1?: number
    ) =>
      [...queryKeys.employees.all, 'company', companyId, 'roles', { roleId, roleId1 }] as const,
    details: () => [...queryKeys.employees.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.employees.details(), id] as const,
  },

  // ─── Agent Companies ─────────────────────────────────────────────────────
  agentCompanies: {
    all: ['agentCompanies'] as const,
    lists: () => [...queryKeys.agentCompanies.all, 'list'] as const,
    list: (companyId?: number) =>
      [...queryKeys.agentCompanies.lists(), { companyId }] as const,
    details: () => [...queryKeys.agentCompanies.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.agentCompanies.details(), id] as const,
  },

  // ─── Agents ───────────────────────────────────────────────────────────────
  agents: {
    all: ['agents'] as const,
    lists: () => [...queryKeys.agents.all, 'list'] as const,
    list: (companyRefId?: number, jobId?: number) =>
      [...queryKeys.agents.lists(), { companyRefId, jobId }] as const,
    details: () => [...queryKeys.agents.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.agents.details(), id] as const,
  },

  // ─── Trucks ───────────────────────────────────────────────────────────────
  trucks: {
    all: ['trucks'] as const,
    lists: () => [...queryKeys.trucks.all, 'list'] as const,
    list: (companyId?: number, searchTerm?: string) =>
      [...queryKeys.trucks.lists(), { companyId, searchTerm }] as const,
    combo: (companyId?: number) =>
      [...queryKeys.trucks.all, 'combo', { companyId }] as const,
    planning: (companyId?: number) =>
      [...queryKeys.trucks.all, 'planning', { companyId }] as const,
    details: () => [...queryKeys.trucks.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.trucks.details(), id] as const,
    /** One truck as the Truck Master form loads it. */
    masterDetail: (id: unknown) => [...queryKeys.trucks.all, 'master-detail', id] as const,
    /** The Truck Master / Truck Maintenance list pages. */
    paginatedAll: () => [...queryKeys.trucks.all, 'paginated'] as const,
    paginated: (payload: unknown) => [...queryKeys.trucks.all, 'paginated', payload] as const,
    /** Drivers a truck can be booked to (Truck Master driver book). */
    fleetDrivers: (companyRefId: number) => [...queryKeys.trucks.all, 'fleet-drivers', companyRefId] as const,
  },

  // ─── Addresses ───────────────────────────────────────────────────────────
  addresses: {
    all: ['addresses'] as const,
    lists: () => [...queryKeys.addresses.all, 'list'] as const,
    list: (companyId?: number) =>
      [...queryKeys.addresses.lists(), { companyId }] as const,
    details: () => [...queryKeys.addresses.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.addresses.details(), id] as const,
  },

  // ─── Products ────────────────────────────────────────────────────────────
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (companyId?: number) =>
      [...queryKeys.products.lists(), { companyId }] as const,
    details: () => [...queryKeys.products.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.products.details(), id] as const,
  },

  // ─── Sale Orders ─────────────────────────────────────────────────────────
  saleOrders: {
    all: ['saleOrders'] as const,
    lists: () => [...queryKeys.saleOrders.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.saleOrders.lists(), { filters }] as const,
    details: () => [...queryKeys.saleOrders.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.saleOrders.details(), id] as const,
  },

  // ─── Planning ───────────────────────────────────────────────────────────
  planning: {
    all: ['planning'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.planning.all, 'list', { filters }] as const,
    maxNo: (companyId: number) =>
      [...queryKeys.planning.all, 'maxNo', companyId] as const,
    detail: (id: string | number) =>
      [...queryKeys.planning.all, 'detail', id] as const,
  },

  // ─── RTI ────────────────────────────────────────────────────────────────
  rti: {
    all: ['rti'] as const,
    lists: () => [...queryKeys.rti.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.rti.lists(), { filters }] as const,
    detail: (id: string | number) =>
      [...queryKeys.rti.all, 'detail', id] as const,
    nextNo: (companyId?: number) =>
      [...queryKeys.rti.all, 'nextNo', { companyId }] as const,
    agents: (companyId?: number, agentCompanyId?: number) =>
      [...queryKeys.rti.all, 'agents', { companyId, agentCompanyId }] as const,
    truckLookup: (companyId?: number, truckId?: string | number) =>
      [...queryKeys.rti.all, 'truckLookup', { companyId, truckId }] as const,
    employees: (companyId?: number) =>
      [...queryKeys.rti.all, 'employees', { companyId }] as const,
    /** Every company's RTI employee list - what an employee save or delete refreshes. */
    employeesAll: () => [...queryKeys.rti.all, 'employees'] as const,
  },

  // ─── Job Types ────────────────────────────────────────────────────────────
  jobTypes: {
    all: ['jobTypes'] as const,
    lists: () => [...queryKeys.jobTypes.all, 'list'] as const,
    list: (companyId?: number) =>
      [...queryKeys.jobTypes.lists(), { companyId }] as const,
  },

  // ─── Job Statuses ─────────────────────────────────────────────────────────
  jobStatuses: {
    all: ['jobStatuses'] as const,
    list: (companyId?: number) =>
      [...queryKeys.jobStatuses.all, 'list', { companyId }] as const,
  },

  // ─── Job Orders (Modern) ────────────────────────────────────────────────
  jobOrders: {
    all: ['jobOrders'] as const,
    statuses: () => [...queryKeys.jobOrders.all, 'statuses'] as const,
    types: () => [...queryKeys.jobOrders.all, 'types'] as const,
    priorities: () => [...queryKeys.jobOrders.all, 'priorities'] as const,
  },

  // ─── Ports ────────────────────────────────────────────────────────────────
  ports: {
    all: ['ports'] as const,
    lists: () => [...queryKeys.ports.all, 'list'] as const,
    list: (companyId?: number) =>
      [...queryKeys.ports.lists(), { companyId }] as const,
  },

  // ─── Tax ─────────────────────────────────────────────────────────────────
  tax: {
    all: ['tax'] as const,
    lists: () => [...queryKeys.tax.all, 'list'] as const,
    list: (companyId?: number) =>
      [...queryKeys.tax.lists(), { companyId }] as const,
  },

  // ─── Drivers ──────────────────────────────────────────────────────────────
  drivers: {
    all: ['drivers'] as const,
    lists: () => [...queryKeys.drivers.all, 'list'] as const,
    list: (companyId?: number, type?: string) =>
      [...queryKeys.drivers.lists(), { companyId, type }] as const,
    details: () => [...queryKeys.drivers.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.drivers.details(), id] as const,
    /** One driver as the Driver Master form loads it. */
    masterDetail: (id: unknown) => [...queryKeys.drivers.all, 'master-detail', id] as const,
    /**
     * The Driver Master list page. Its own root ('drivers-paginated'), as it
     * always had: a drivers.all refresh does not reach it, so driver saves and
     * deletes refresh both.
     */
    paginatedAll: () => ['drivers-paginated'] as const,
    paginated: (payload: unknown) => ['drivers-paginated', payload] as const,
  },

  // ─── Operations ───────────────────────────────────────────────────────────
  operations: {
    all: ['operations'] as const,
    lists: () => [...queryKeys.operations.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.operations.lists(), { filters }] as const,
    details: () => [...queryKeys.operations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.operations.details(), id] as const,
  },

  // ─── Vehicles ─────────────────────────────────────────────────────────────
  vehicles: {
    all: ['vehicles'] as const,
    lists: () => [...queryKeys.vehicles.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.vehicles.lists(), { filters }] as const,
    details: () => [...queryKeys.vehicles.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.vehicles.details(), id] as const,
  },

  // ─── Shipments ────────────────────────────────────────────────────────────
  shipments: {
    all: ['shipments'] as const,
    lists: () => [...queryKeys.shipments.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.shipments.lists(), { filters }] as const,
    details: () => [...queryKeys.shipments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.shipments.details(), id] as const,
  },

  // ─── Inventory ────────────────────────────────────────────────────────────
  inventory: {
    all: ['inventory'] as const,
    lists: () => [...queryKeys.inventory.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.inventory.lists(), { filters }] as const,
    details: () => [...queryKeys.inventory.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.inventory.details(), id] as const,
  },

  // ─── Alerts ───────────────────────────────────────────────────────────────
  alerts: {
    all: ['alerts'] as const,
    lists: () => [...queryKeys.alerts.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.alerts.all, 'list', { filters }] as const,
  },

  // ─── Payment Terms ─────────────────────────────────────────────────────────
  paymentTerms: {
    all: ['paymentTerms'] as const,
    lists: () => [...queryKeys.paymentTerms.all, 'list'] as const,
    list: (companyId?: number) =>
      [...queryKeys.paymentTerms.lists(), { companyId }] as const,
  },

  // ─── Job Numbers ───────────────────────────────────────────────────────────
  jobNumbers: {
    all: ['jobNumbers'] as const,
    list: (companyId?: number, billType?: string) =>
      [...queryKeys.jobNumbers.all, 'list', { companyId, billType }] as const,
  },

  // ─── Active Addresses ─────────────────────────────────────────────────────
  activeAddresses: {
    all: ['activeAddresses'] as const,
    list: (companyId?: number) =>
      [...queryKeys.activeAddresses.all, 'list', { companyId }] as const,
  },

  // ─── Locations ────────────────────────────────────────────────────────────
  locations: {
    all: ['locations'] as const,
    lists: () => [...queryKeys.locations.all, 'list'] as const,
    list: (companyId?: number) =>
      [...queryKeys.locations.lists(), { companyId }] as const,
  },
};

// ─── Default Export ────────────────────────────────────────────────────────────
// Single QueryClient instance using the factory
export const queryClient = createQueryClient();

export default queryClient;
